import type { ColumnProfile } from '@/cache/stats';

/**
 * Per-column sparkline.
 *
 * Density is the point: these are read pre-attentively, in a stack of twenty,
 * at 76x18 px. So there is no axis, no legend, no gridline and no second hue --
 * one mark colour carries the distribution and the eye compares shapes down the
 * column. Detail lives one click away in ColumnDetail.
 */

export interface SparklineProps {
  readonly profile: ColumnProfile;
  readonly width?: number;
  readonly height?: number;
  readonly interactive?: boolean;
}

const MARK = 'var(--c-local)';

export function Sparkline({
  profile,
  width = 76,
  height = 18,
  interactive = false,
}: SparklineProps): JSX.Element {
  const title = describe(profile);

  if (profile.kind === 'histogram' || profile.kind === 'timeseries') {
    const bins = profile.bins;
    const peak = bins.reduce((max, bin) => Math.max(max, bin.count), 0);

    if (peak === 0) return <Blank width={width} height={height} label={title} />;

    const slot = width / Math.max(1, bins.length);
    const barWidth = Math.max(1, slot - 1);

    return (
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={title}
        className="overflow-visible"
        data-testid="sparkline"
      >
        {bins.map((bin) => {
          const barHeight = bin.count === 0 ? 0 : Math.max(1, (bin.count / peak) * height);
          return (
            <rect
              key={bin.index}
              x={bin.index * slot}
              y={height - barHeight}
              width={barWidth}
              height={barHeight}
              fill={MARK}
              opacity={0.85}
            >
              {interactive ? <title>{`${bin.label}: ${bin.count.toLocaleString()}`}</title> : null}
            </rect>
          );
        })}
      </svg>
    );
  }

  if (profile.kind === 'categories' || profile.kind === 'boolean') {
    const top = profile.top.slice(0, 8);
    const peak = top.reduce((max, entry) => Math.max(max, entry.count), 0);

    if (peak === 0) return <Blank width={width} height={height} label={title} />;

    const slot = height / Math.max(1, top.length);
    const barHeight = Math.max(1, slot - 1);

    // Horizontal bars: category names are ordinal, and length-along-a-common-
    // baseline is the most accurate encoding for comparing them.
    return (
      <svg width={width} height={height} role="img" aria-label={title} data-testid="sparkline">
        {top.map((entry, index) => (
          <rect
            key={`${entry.value ?? 'null'}-${index}`}
            x={0}
            y={index * slot}
            width={Math.max(1, (entry.count / peak) * width)}
            height={barHeight}
            fill={entry.isNull ? 'var(--c-faint)' : MARK}
            opacity={entry.isNull ? 0.6 : 0.85}
          >
            {interactive ? (
              <title>{`${entry.isNull ? 'NULL' : entry.isEmpty ? '(empty)' : entry.value}: ${entry.count.toLocaleString()}`}</title>
            ) : null}
          </rect>
        ))}
      </svg>
    );
  }

  return <Blank width={width} height={height} label={title} />;
}

function Blank({ width, height, label }: { width: number; height: number; label: string }): JSX.Element {
  return (
    <svg width={width} height={height} role="img" aria-label={label} data-testid="sparkline">
      <line
        x1={0}
        y1={height - 1}
        x2={width}
        y2={height - 1}
        stroke="var(--c-line-strong)"
        strokeDasharray="2 2"
      />
    </svg>
  );
}

function describe(profile: ColumnProfile): string {
  switch (profile.kind) {
    case 'histogram':
      return `Distribution of ${profile.column}, ${profile.min ?? '?'} to ${profile.max ?? '?'}`;
    case 'timeseries':
      return `${profile.column} over time, ${profile.min ?? '?'} to ${profile.max ?? '?'}`;
    case 'boolean':
    case 'categories':
      return `Top values of ${profile.column}, ${profile.distinct} distinct`;
    default:
      return `No distribution available for ${profile.column}`;
  }
}
